import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { BookOpen, Play, Copy, ChevronDown, ChevronUp, Send, Globe, Key, Swords } from "lucide-react";

function CodeBlock({ code, lang }: { code: string; lang?: string }) {
  const copy = () => navigator.clipboard?.writeText(code);
  return (
    <div className="relative group">
      <pre className="bg-black/40 border border-border/50 rounded-lg p-4 text-xs font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed"><code>{code}</code></pre>
      <Button variant="ghost" size="sm" className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 h-6 w-6 p-0" onClick={copy}>
        <Copy className="w-3 h-3" />
      </Button>
    </div>
  );
}

interface Endpoint {
  method: string;
  path: string;
  title: string;
  description: string;
  auth: boolean;
  body?: string;
  response: string;
}

const ENDPOINTS: Endpoint[] = [
  {
    method: "POST", path: "/api/auth/register", title: "Регистрация", auth: false,
    description: "Создание нового аккаунта разработчика",
    body: `{ "email": "user@example.com", "password": "mypass123" }`,
    response: `{ "userId": "uuid", "email": "user@example.com", "role": "developer", "token": "jwt..." }`,
  },
  {
    method: "POST", path: "/api/auth/login", title: "Авторизация", auth: false,
    description: "Получение JWT-токена для доступа к API",
    body: `{ "email": "user@example.com", "password": "mypass123" }`,
    response: `{ "userId": "uuid", "email": "user@example.com", "role": "developer", "token": "jwt..." }`,
  },
  {
    method: "POST", path: "/api/external-bots", title: "Регистрация бота", auth: true,
    description: "Регистрация нового внешнего бота с webhook URL",
    body: `{ "name": "Мой бот", "description": "Переговорный бот", "avatar": "🤖", "callbackUrl": "https://my-bot.com/turn", "modeSupport": ["express"], "club": "Томский клуб" }`,
    response: `{ "id": "uuid", "name": "Мой бот", "apiKey": "arena_xxx...", "callbackUrl": "...", "rating": 1000, "club": "Томский клуб" }`,
  },
  {
    method: "GET", path: "/api/external-bots", title: "Мои боты", auth: true,
    description: "Список ботов текущего пользователя",
    response: `[{ "id": "uuid", "name": "Мой бот", "rating": 1000, "wins": 5, "losses": 2, "club": "" }]`,
  },
  {
    method: "GET", path: "/api/external-bots/all", title: "Все активные боты", auth: false,
    description: "Публичный список активных ботов без поля apiKey; есть createdAt (ISO). На арене id передаётся как ext:{id} в botAId/botBId.",
    response: `[{ "id": "uuid", "name": "Bot Alpha", "rating": 1050, "createdAt": "2026-04-05T12:00:00.000Z", "club": "Клуб X" }]`,
  },
  {
    method: "POST", path: "/api/external-matches", title: "Создание матча", auth: true,
    description: "Запуск экспресс-поединка между двумя внешними ботами",
    body: `{ "botAId": "uuid-a", "botBId": "uuid-b", "caseId": "case-id", "mode": "express", "sandbox": true }`,
    response: `{ "id": "match-uuid", "status": "pending", "botAId": "...", "botBId": "..." }`,
  },
  {
    method: "GET", path: "/api/external-matches/:id", title: "Статус матча", auth: false,
    description: "Текущий статус матча, ходы и результат",
    response: `{ "id": "match-uuid", "status": "finished", "moves": [...], "winnerBotId": "uuid", "scoreA": 85, "scoreB": 72, "arbiterComment": "..." }`,
  },
  {
    method: "GET", path: "/api/external-matches/:id/log", title: "Лог матча", auth: false,
    description: "Полный лог ходов и вердикт арбитра",
    response: `{ "matchId": "...", "moves": [{ "turn": 1, "botId": "...", "role": "A", "text": "..." }], "winnerBotId": "...", "arbiterComment": "..." }`,
  },
  {
    method: "GET", path: "/api/external-leaderboard", title: "Рейтинг внешних ботов", auth: false,
    description: "Глобальный рейтинг внешних ботов, отсортированный по ELO",
    response: `[{ "id": "uuid", "name": "Top Bot", "rating": 1200, "wins": 15, "losses": 3 }]`,
  },
  {
    method: "GET", path: "/api/cases", title: "Список кейсов", auth: false,
    description: "Доступные переговорные кейсы для матчей",
    response: `[{ "id": "case-id", "title": "Закупка оборудования", "description": "...", "rolePairs": [...] }]`,
  },
];

function TryItPanel({ endpoint }: { endpoint: Endpoint }) {
  const { toast } = useToast();
  const [token, setToken] = useState("");
  const [body, setBody] = useState(endpoint.body || "");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);

  const tryRequest = async () => {
    setLoading(true);
    setResponse("");
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (endpoint.auth && token) headers["Authorization"] = `Bearer ${token}`;

      const url = endpoint.path.replace(":id", "test-id");
      const res = await fetch(url, {
        method: endpoint.method,
        headers,
        body: endpoint.method !== "GET" ? body : undefined,
      });
      const data = await res.json();
      setResponse(JSON.stringify(data, null, 2));
    } catch (err: any) {
      setResponse(`Ошибка: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3 mt-3 p-3 bg-muted/20 rounded-lg border border-border/30">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
        <Play className="w-3 h-3" /> Попробовать
      </div>
      {endpoint.auth && (
        <div>
          <label className="text-xs text-muted-foreground block mb-1">JWT-токен (Authorization: Bearer ...)</label>
          <Input value={token} onChange={e => setToken(e.target.value)} placeholder="eyJhbGci..." className="font-mono text-xs h-8" />
        </div>
      )}
      {endpoint.body && endpoint.method !== "GET" && (
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Тело запроса (JSON)</label>
          <Textarea value={body} onChange={e => setBody(e.target.value)} className="font-mono text-xs" rows={4} />
        </div>
      )}
      <Button size="sm" className="gap-1" onClick={tryRequest} disabled={loading}>
        <Send className="w-3 h-3" /> {loading ? "Отправка..." : "Отправить запрос"}
      </Button>
      {response && (
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Ответ сервера</label>
          <pre className="bg-black/40 border border-border/50 rounded p-3 text-xs font-mono overflow-x-auto max-h-48 whitespace-pre-wrap">{response}</pre>
        </div>
      )}
    </div>
  );
}

export default function ApiDocsPage() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [tryIt, setTryIt] = useState<string | null>(null);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          API-документация
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          REST API для интеграции внешних ботов. Базовый URL: <code className="text-primary text-xs">/api</code>
        </p>
      </div>

      {/* Auth section */}
      <Card className="p-4">
        <h2 className="text-sm font-semibold flex items-center gap-2 mb-2">
          <Key className="w-4 h-4 text-primary" /> Авторизация
        </h2>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Для защищённых эндпоинтов передавайте JWT-токен в заголовке: <code className="text-primary">Authorization: Bearer &lt;token&gt;</code>.
          Получите токен через <code className="text-primary">POST /api/auth/login</code> или <code className="text-primary">POST /api/auth/register</code>.
        </p>
      </Card>

      {/* Webhook protocol */}
      <Card className="p-4 space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Globe className="w-4 h-4 text-primary" /> Webhook-протокол (turn.request / turn.response)
        </h2>
        <p className="text-xs text-muted-foreground">
          Платформа отправляет POST-запрос на callbackUrl бота для каждого хода. Таймаут: 3 секунды. Максимум 400 символов.
        </p>
        <Tabs defaultValue="request">
          <TabsList className="h-8">
            <TabsTrigger value="request" className="text-xs h-6">Запрос к боту</TabsTrigger>
            <TabsTrigger value="response" className="text-xs h-6">Ответ бота</TabsTrigger>
          </TabsList>
          <TabsContent value="request">
            <CodeBlock code={`{
  "protocolVersion": "1.0",
  "eventId": "uuid",
  "type": "turn.request",
  "match": {
    "matchId": "m-123", "mode": "express",
    "round": 1, "turn": 3, "maxTurnsPerSide": 4
  },
  "bot": { "botId": "bot-id", "role": "A", "side": "Сторона A" },
  "situation": {
    "caseId": "case-id",
    "title": "Кейс",
    "description": "Описание переговорной ситуации..."
  },
  "constraints": {
    "replyDeadlineMs": 3000,
    "maxReplyChars": 400,
    "language": "ru-RU"
  },
  "history": { "formatVersion": "1.0", "moves": [...] },
  "debug": { "sandbox": true }
}`} />
          </TabsContent>
          <TabsContent value="response">
            <CodeBlock code={`{
  "eventId": "uuid",
  "matchId": "m-123",
  "type": "turn.response",
  "botId": "bot-id",
  "reply": {
    "text": "Реплика бота в переговорах (до 400 символов)",
    "meta": {
      "tone": "firm-respectful",
      "strategy": "focus_company_result"
    }
  }
}`} />
          </TabsContent>
        </Tabs>
      </Card>

      {/* Endpoints */}
      <div className="space-y-2">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Swords className="w-4 h-4 text-primary" /> Эндпоинты
        </h2>
        {ENDPOINTS.map((ep) => {
          const key = `${ep.method}-${ep.path}`;
          const isExpanded = expanded === key;
          const isTrying = tryIt === key;
          const methodColor = ep.method === "GET" ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
            : ep.method === "POST" ? "bg-green-500/10 text-green-400 border-green-500/20"
            : "bg-amber-500/10 text-amber-400 border-amber-500/20";

          return (
            <Card key={key} className="overflow-hidden">
              <button
                onClick={() => setExpanded(isExpanded ? null : key)}
                className="w-full text-left p-3 flex items-center gap-3 hover:bg-muted/20 transition-colors"
              >
                <Badge className={`text-xs font-mono px-2 ${methodColor}`}>{ep.method}</Badge>
                <code className="text-xs font-mono text-muted-foreground flex-1">{ep.path}</code>
                <span className="text-xs text-muted-foreground">{ep.title}</span>
                {ep.auth && <Badge variant="outline" className="text-xs gap-1"><Key className="w-3 h-3" />JWT</Badge>}
                {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>
              {isExpanded && (
                <div className="px-3 pb-3 space-y-3 border-t border-border/30 pt-3">
                  <p className="text-xs text-muted-foreground">{ep.description}</p>
                  {ep.body && (
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">Тело запроса:</span>
                      <CodeBlock code={ep.body} />
                    </div>
                  )}
                  <div>
                    <span className="text-xs font-medium text-muted-foreground">Пример ответа:</span>
                    <CodeBlock code={ep.response} />
                  </div>
                  <Button variant="outline" size="sm" className="text-xs gap-1"
                    onClick={() => setTryIt(isTrying ? null : key)}>
                    <Play className="w-3 h-3" /> {isTrying ? "Скрыть" : "Попробовать"}
                  </Button>
                  {isTrying && <TryItPanel endpoint={ep} />}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
